import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { SignIn, ChartLine, Users, CheckCircle, HardDrives, Globe, TrendUp } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'gcd-admin-token';

const COLORS = ['#facc15', '#0ea5e9', '#f472b6', '#22c55e', '#8b5cf6', '#f97316', '#14b8a6', '#ef4444'];

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="border-2 border-black bg-white p-5 brutal-shadow-sm">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 grid place-items-center border-2 border-black bg-yellow-300">
          <Icon weight="bold" size={18} />
        </span>
        <p className="font-mono-plex text-xs uppercase tracking-widest text-zinc-500">{label}</p>
      </div>
      <p className="font-display font-bold text-3xl md:text-4xl mt-3">{value}</p>
      {sub && <p className="text-xs font-mono-plex text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
  });
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [range, setRange] = useState('day');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Admin Dashboard | Go Convert Documents';
    // Prevent search-engine indexing of the admin route
    let robots = document.querySelector('meta[name="robots"]');
    const prev = robots ? robots.getAttribute('content') : null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (prev !== null) robots.setAttribute('content', prev);
    };
  }, []);

  const login = async () => {
    if (!password.trim()) { toast.error('Enter the admin password'); return; }
    setLoggingIn(true);
    try {
      const fd = new FormData();
      fd.append('password', password);
      const r = await axios.post(`${API}/admin/login`, fd);
      const t = r.data.token;
      sessionStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      toast.success('Signed in');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sign-in failed');
    } finally { setLoggingIn(false); }
  };

  const fetchStats = async (t = token, r = range) => {
    if (!t) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/stats?range=${r}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setStats(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken('');
        toast.error('Session expired — please sign in again');
      } else {
        toast.error('Failed to load stats');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) fetchStats(token, range); /* eslint-disable-line */ }, [token, range]);

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-24">
        <div className="border-2 border-black bg-white p-8 brutal-shadow">
          <p className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500">Admin</p>
          <h1 className="font-display font-bold text-3xl md:text-4xl mt-2 text-slate-950">
            Sign in
          </h1>
          <p className="text-sm text-zinc-600 mt-2">
            Enter the admin password to view analytics. Regular users don't need to sign in — the converter tools stay open to everyone.
          </p>
          <input
            data-testid="admin-password-input"
            type="password"
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && login()}
            className="w-full mt-6 px-4 py-3 border-2 border-black bg-white font-mono-plex text-sm focus:outline-none"
          />
          <button
            data-testid="admin-signin-btn"
            onClick={login}
            disabled={loggingIn}
            className="brutal-btn mt-4 w-full inline-flex items-center justify-center gap-2 disabled:opacity-40"
          >
            <SignIn weight="bold" size={16} /> {loggingIn ? 'Signing in…' : 'Sign in'}
          </button>
        </div>
      </div>
    );
  }

  const t = stats?.totals || { conversions: 0, success: 0, failed: 0, unique_visitors: 0, avg_file_kb: 0, success_rate: 0 };
  const tools = stats?.tools || [];
  const countries = stats?.countries || [];
  const timeline = stats?.timeline || [];

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500">Admin dashboard</p>
          <h1 data-testid="admin-title" className="font-display font-bold text-4xl md:text-5xl text-slate-950 mt-2">
            Analytics
          </h1>
          <p className="text-zinc-600 mt-2">Anonymised conversion metrics for the last {range === 'day' ? '24 hours' : range === 'week' ? '7 days' : '30 days'}.</p>
        </div>
        <div className="flex gap-2">
          {['day', 'week', 'month'].map((r) => (
            <button
              key={r}
              data-testid={`range-${r}`}
              onClick={() => setRange(r)}
              className={`px-4 py-2 border-2 border-black text-sm font-semibold ${range === r ? 'bg-black text-white' : 'bg-white'}`}
              style={{ transition: 'background-color 0.15s ease, color 0.15s ease' }}
            >
              {r === 'day' ? 'Today' : r === 'week' ? 'This week' : 'This month'}
            </button>
          ))}
          <button
            data-testid="admin-signout"
            onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(''); }}
            className="px-4 py-2 border-2 border-black text-sm font-semibold bg-white hover:bg-rose-500 hover:text-white"
            style={{ transition: 'background-color 0.15s ease, color 0.15s ease' }}
          >
            Sign out
          </button>
        </div>
      </div>

      {loading && !stats ? (
        <div className="mt-10 h-40 border-2 border-black bg-zinc-100 animate-pulse" />
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="admin-stat-cards">
            <StatCard icon={ChartLine} label="Conversions" value={t.conversions.toLocaleString()} sub={`${t.success} success · ${t.failed} failed`} />
            <StatCard icon={Users} label="Unique visitors" value={t.unique_visitors.toLocaleString()} sub="Distinct IP hashes" />
            <StatCard icon={CheckCircle} label="Success rate" value={`${t.success_rate}%`} sub={t.failed ? `${t.failed} failed` : 'All succeeded'} />
            <StatCard icon={HardDrives} label="Avg file size" value={`${t.avg_file_kb.toLocaleString()} KB`} sub="Successful conversions" />
          </div>

          {/* Timeline */}
          <div className="mt-10 border-2 border-black bg-white p-6 brutal-shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <TrendUp weight="bold" size={18} />
              <p className="font-display font-semibold text-lg">Conversions over time</p>
            </div>
            {timeline.length ? (
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={timeline}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" fontSize={12} />
                    <YAxis fontSize={12} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#0a0a0a" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-zinc-500 font-mono-plex py-12 text-center">
                No conversions in this window yet — as usage grows, this chart will fill in.
              </p>
            )}
          </div>

          {/* Tool breakdown + Country breakdown */}
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div className="border-2 border-black bg-white p-6 brutal-shadow-sm">
              <p className="font-display font-semibold text-lg mb-4">Top tools used</p>
              {tools.length ? (
                <div style={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={tools} layout="vertical" margin={{ left: 24 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" fontSize={12} allowDecimals={false} />
                      <YAxis dataKey="tool" type="category" fontSize={11} width={110} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#facc15" stroke="#0a0a0a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-zinc-500 font-mono-plex py-12 text-center">
                  No tool usage data yet in this window.
                </p>
              )}
            </div>

            <div className="border-2 border-black bg-white p-6 brutal-shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Globe weight="bold" size={18} />
                <p className="font-display font-semibold text-lg">Countries</p>
              </div>
              <div style={{ width: '100%', height: 320 }}>
                {countries.length ? (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={countries} dataKey="count" nameKey="country" outerRadius={110} label>
                        {countries.map((c, i) => (
                          <Cell key={c.country} fill={COLORS[i % COLORS.length]} stroke="#0a0a0a" />
                        ))}
                      </Pie>
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-zinc-500 font-mono-plex">No country data yet. Cloudflare sets CF-IPCountry in production.</p>
                )}
              </div>
            </div>
          </div>

          {/* Raw tables */}
          <div className="grid md:grid-cols-2 gap-6 mt-6" data-testid="admin-tables">
            <div className="border-2 border-black bg-white p-6 brutal-shadow-sm">
              <p className="font-display font-semibold text-lg mb-4">Tool leaderboard</p>
              <table className="w-full text-sm font-mono-plex">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left py-2">Tool</th>
                    <th className="text-right py-2">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {tools.map((row) => (
                    <tr key={row.tool} className="border-b border-zinc-200">
                      <td className="py-1.5">{row.tool}</td>
                      <td className="text-right">{row.count}</td>
                    </tr>
                  ))}
                  {!tools.length && <tr><td colSpan="2" className="py-4 text-zinc-500">No data in this window.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="border-2 border-black bg-white p-6 brutal-shadow-sm">
              <p className="font-display font-semibold text-lg mb-4">Country leaderboard</p>
              <table className="w-full text-sm font-mono-plex">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left py-2">Country</th>
                    <th className="text-right py-2">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {countries.map((row) => (
                    <tr key={row.country} className="border-b border-zinc-200">
                      <td className="py-1.5">{row.country}</td>
                      <td className="text-right">{row.count}</td>
                    </tr>
                  ))}
                  {!countries.length && <tr><td colSpan="2" className="py-4 text-zinc-500">No country data.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
